import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  doc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Post, NewPost, Group } from '../types';
import { deleteFromR2, getKeyFromUrl } from './r2Storage';

const POSTS_PER_PAGE = 10;

interface FetchPostsResult {
  posts: Post[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

export async function fetchPosts(
  lastDoc?: QueryDocumentSnapshot | null
): Promise<FetchPostsResult> {
  const postsRef = collection(db, 'posts');

  let q = query(postsRef, orderBy('createdAt', 'desc'), limit(POSTS_PER_PAGE));

  if (lastDoc) {
    q = query(
      postsRef,
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(POSTS_PER_PAGE)
    );
  }

  const snapshot = await getDocs(q);

  // Process all posts in parallel instead of sequentially
  const postsPromises = snapshot.docs.map(async (docSnap) => {
    const postData = docSnap.data();
    const post: Post = {
      id: docSnap.id,
      title: postData.title,
      description: postData.description,
      image: postData.image,
      images: postData.images || (postData.image ? [postData.image] : undefined), // Support both old and new format
      video: postData.video,
      link: postData.link,
      groupId: postData.groupId,
      userId: postData.userId,
      createdAt: postData.createdAt,
    };

    // Fetch group, upvotes, and comments in parallel
    const [groupResult, upvotesSnapshot, commentsSnapshot] = await Promise.all([
      // Fetch group data
      postData.groupId
        ? getDoc(doc(db, 'groups', postData.groupId))
        : Promise.resolve(null),
      // Fetch upvotes
      getDocs(query(collection(db, 'upvotes'), where('postId', '==', docSnap.id))),
      // Fetch comments count
      getDocs(query(collection(db, 'comments'), where('postId', '==', docSnap.id))),
    ]);

    // Process group
    if (groupResult?.exists()) {
      post.group = { id: groupResult.id, ...groupResult.data() } as Group;
    }

    // Calculate upvotes
    post.upvotesSum = upvotesSnapshot.docs.reduce(
      (sum, voteDoc) => sum + (voteDoc.data().value || 0),
      0
    );

    let upvotesCount = 0;
    let downvotesCount = 0;
    upvotesSnapshot.docs.forEach(voteDoc => {
      const value = voteDoc.data().value;
      if (value === 1) upvotesCount++;
      else if (value === -1) downvotesCount++;
    });
    post.upvotes = upvotesCount;
    post.downvotes = downvotesCount;

    // Set comments count
    post.commentsCount = commentsSnapshot.docs.length;

    return post;
  });

  const posts = await Promise.all(postsPromises);

  const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

  return {
    posts,
    lastDoc: lastVisible,
    hasMore: snapshot.docs.length === POSTS_PER_PAGE,
  };
}

export async function fetchPostsByGroupId(
  groupId: string,
  lastDoc?: QueryDocumentSnapshot | null
): Promise<FetchPostsResult> {
  const postsRef = collection(db, 'posts');

  let q = query(
    postsRef,
    where('groupId', '==', groupId),
    orderBy('createdAt', 'desc'),
    limit(POSTS_PER_PAGE)
  );

  if (lastDoc) {
    q = query(
      postsRef,
      where('groupId', '==', groupId),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(POSTS_PER_PAGE)
    );
  }

  const snapshot = await getDocs(q);

  // Process all posts in parallel instead of sequentially
  const postsPromises = snapshot.docs.map(async (docSnap) => {
    const postData = docSnap.data();
    const post: Post = {
      id: docSnap.id,
      title: postData.title,
      description: postData.description,
      image: postData.image,
      images: postData.images || (postData.image ? [postData.image] : undefined), // Support both old and new format
      video: postData.video,
      link: postData.link,
      groupId: postData.groupId,
      userId: postData.userId,
      createdAt: postData.createdAt,
    };

    // Fetch upvotes and comments in parallel
    const [upvotesSnapshot, commentsSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'upvotes'), where('postId', '==', docSnap.id))),
      getDocs(query(collection(db, 'comments'), where('postId', '==', docSnap.id))),
    ]);

    // Calculate upvotes
    post.upvotesSum = upvotesSnapshot.docs.reduce(
      (sum, voteDoc) => sum + (voteDoc.data().value || 0),
      0
    );

    let upvotesCount = 0;
    let downvotesCount = 0;
    upvotesSnapshot.docs.forEach(voteDoc => {
      const value = voteDoc.data().value;
      if (value === 1) upvotesCount++;
      else if (value === -1) downvotesCount++;
    });
    post.upvotes = upvotesCount;
    post.downvotes = downvotesCount;

    // Set comments count
    post.commentsCount = commentsSnapshot.docs.length;

    return post;
  });

  const posts = await Promise.all(postsPromises);

  const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

  return {
    posts,
    lastDoc: lastVisible,
    hasMore: snapshot.docs.length === POSTS_PER_PAGE,
  };
}

export async function fetchPostsByUserId(
  userId: string,
  lastDoc?: QueryDocumentSnapshot | null
): Promise<FetchPostsResult> {
  const postsRef = collection(db, 'posts');

  let q = query(
    postsRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(POSTS_PER_PAGE)
  );

  if (lastDoc) {
    q = query(
      postsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(POSTS_PER_PAGE)
    );
  }

  const snapshot = await getDocs(q);

  // Process all posts in parallel
  const postsPromises = snapshot.docs.map(async (docSnap) => {
    const postData = docSnap.data();
    const post: Post = {
      id: docSnap.id,
      title: postData.title,
      description: postData.description,
      image: postData.image,
      images: postData.images || (postData.image ? [postData.image] : undefined),
      video: postData.video,
      link: postData.link,
      groupId: postData.groupId,
      userId: postData.userId,
      createdAt: postData.createdAt,
    };

    // Fetch group, upvotes, and comments in parallel
    const [groupResult, upvotesSnapshot, commentsSnapshot] = await Promise.all([
      postData.groupId
        ? getDoc(doc(db, 'groups', postData.groupId))
        : Promise.resolve(null),
      getDocs(query(collection(db, 'upvotes'), where('postId', '==', docSnap.id))),
      getDocs(query(collection(db, 'comments'), where('postId', '==', docSnap.id))),
    ]);

    // Process group
    if (groupResult?.exists()) {
      post.group = { id: groupResult.id, ...groupResult.data() } as Group;
    }

    // Calculate upvotes
    post.upvotesSum = upvotesSnapshot.docs.reduce(
      (sum, voteDoc) => sum + (voteDoc.data().value || 0),
      0
    );

    let upvotesCount = 0;
    let downvotesCount = 0;
    upvotesSnapshot.docs.forEach(voteDoc => {
      const value = voteDoc.data().value;
      if (value === 1) upvotesCount++;
      else if (value === -1) downvotesCount++;
    });
    post.upvotes = upvotesCount;
    post.downvotes = downvotesCount;

    // Set comments count
    post.commentsCount = commentsSnapshot.docs.length;

    return post;
  });

  const posts = await Promise.all(postsPromises);

  const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

  return {
    posts,
    lastDoc: lastVisible,
    hasMore: snapshot.docs.length === POSTS_PER_PAGE,
  };
}

/**
 * Fetches total karma (sum of all upvotes) for a user's posts
 */
export async function fetchUserKarma(userId: string): Promise<number> {
  const postsRef = collection(db, 'posts');
  const postsQuery = query(postsRef, where('userId', '==', userId));
  const postsSnapshot = await getDocs(postsQuery);

  let totalKarma = 0;

  // Get all post IDs
  const postIds = postsSnapshot.docs.map(doc => doc.id);

  // Fetch upvotes for all posts in parallel
  const upvotesPromises = postIds.map(postId =>
    getDocs(query(collection(db, 'upvotes'), where('postId', '==', postId)))
  );

  const upvotesSnapshots = await Promise.all(upvotesPromises);

  upvotesSnapshots.forEach(snapshot => {
    snapshot.docs.forEach(voteDoc => {
      totalKarma += voteDoc.data().value || 0;
    });
  });

  return totalKarma;
}

/**
 * Fetches total post count for a user
 */
export async function fetchUserPostCount(userId: string): Promise<number> {
  const postsRef = collection(db, 'posts');
  const postsQuery = query(postsRef, where('userId', '==', userId));
  const postsSnapshot = await getDocs(postsQuery);
  return postsSnapshot.docs.length;
}

export async function fetchPostById(id: string, options?: { skipCommentsCount?: boolean }): Promise<Post | null> {
  const docRef = doc(db, 'posts', id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const postData = docSnap.data();
  const post: Post = {
    id: docSnap.id,
    title: postData.title,
    description: postData.description,
    image: postData.image,
    images: postData.images || (postData.image ? [postData.image] : undefined), // Support both old and new format
    video: postData.video,
    link: postData.link,
    groupId: postData.groupId,
    userId: postData.userId,
    createdAt: postData.createdAt,
  };

  // Fetch group, user, and upvotes in parallel (skip comments if requested)
  const fetchPromises: Promise<any>[] = [
    // Fetch group data
    postData.groupId
      ? getDoc(doc(db, 'groups', postData.groupId))
      : Promise.resolve(null),
    // Fetch user data
    postData.userId
      ? getDoc(doc(db, 'users', postData.userId))
      : Promise.resolve(null),
    // Fetch upvotes
    getDocs(query(collection(db, 'upvotes'), where('postId', '==', docSnap.id))),
  ];

  // Only fetch comments count if not skipped
  if (!options?.skipCommentsCount) {
    fetchPromises.push(
      getDocs(query(collection(db, 'comments'), where('postId', '==', docSnap.id)))
    );
  }

  const results = await Promise.all(fetchPromises);
  const [groupResult, userResult, upvotesSnapshot, commentsSnapshot] = results;

  // Process group
  if (groupResult?.exists?.()) {
    post.group = { id: groupResult.id, ...groupResult.data() } as Group;
  }

  // Process user
  if (userResult?.exists?.()) {
    post.user = { id: userResult.id, ...userResult.data() } as any;
  }

  // Calculate upvotes
  post.upvotesSum = upvotesSnapshot.docs.reduce(
    (sum: number, voteDoc: any) => sum + (voteDoc.data().value || 0),
    0
  );

  // Set comments count (from cached data or fetched)
  if (commentsSnapshot) {
    post.commentsCount = commentsSnapshot.docs.length;
  }

  return post;
}

export async function insertPost(
  newPost: NewPost,
  userId: string
): Promise<string> {
  // Firestore does not allow fields with value `undefined`.
  // Build the data object and strip out any undefined values before saving.
  const data: any = {
    ...newPost,
    userId,
    createdAt: serverTimestamp(),
  };

  Object.keys(data).forEach((key) => {
    if (data[key] === undefined) {
      delete data[key];
    }
  });

  const docRef = await addDoc(collection(db, 'posts'), data);
  return docRef.id;
}

export async function deletePostById(id: string): Promise<void> {
  // First, fetch the post to get media URLs
  const docRef = doc(db, 'posts', id);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const postData = docSnap.data();
    const deletePromises: Promise<void>[] = [];

    // Collect all media URLs to delete
    const mediaUrls: string[] = [];

    // Handle video
    if (postData.video) {
      mediaUrls.push(postData.video);
    }

    // Handle images array
    if (postData.images && Array.isArray(postData.images)) {
      mediaUrls.push(...postData.images);
    } else if (postData.image) {
      // Handle legacy single image field
      mediaUrls.push(postData.image);
    }

    // Delete each media file from R2
    for (const url of mediaUrls) {
      const key = getKeyFromUrl(url);
      if (key) {
        // Don't let R2 deletion failure prevent post deletion
        deletePromises.push(
          deleteFromR2(key).catch((err) => {
            console.warn(`Failed to delete R2 file ${key}:`, err);
          })
        );
      }
    }

    // Wait for all R2 deletions to complete (or fail gracefully)
    await Promise.all(deletePromises);
  }

  // Delete the Firestore document
  await deleteDoc(docRef);
}
